import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, FileText, ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import './UsuarioResumosNovo.css';

const UsuarioResumosNovo = () => {
  const navigate = useNavigate();
  const [resumos, setResumos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [busca, setBusca] = useState('');
  const [especialidadeFiltro, setEspecialidadeFiltro] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [especialidadeModal, setEspecialidadeModal] = useState(null);
  const [especialidadeModalOpen, setEspecialidadeModalOpen] = useState(false);
  const [buscaModal, setBuscaModal] = useState('');
  const [resumosModal, setResumosModal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPapel, setUserPapel] = useState(null); // NEW: for desempenho card

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Fixed specialties with images - using OFFICIAL names (single source of truth)
  const especialidadesFixas = [
    { id: 'clinica', nome: 'Clínica Médica', imagem: '/clinica-medica.png', cor: '#dc2626', dbMatch: 'Clínica Médica' },
    { id: 'cirurgia', nome: 'Cirurgia', imagem: '/cirurgia.png', cor: '#2563eb', dbMatch: 'Cirurgia' },
    { id: 'pediatria', nome: 'Pediatria', imagem: '/pediatria.png', cor: '#16a34a', dbMatch: 'Pediatria' },
    { id: 'preventiva', nome: 'Preventiva', imagem: '/preventiva.png', cor: '#9333ea', dbMatch: 'Preventiva' },
    { id: 'gineco', nome: 'Ginecologia e Obstetrícia', imagem: '/ginecologia.png', cor: '#ea580c', dbMatch: 'Ginecologia e Obstetrícia' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    handleSearch();
  }, [busca, especialidadeFiltro]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const [espRes, resumosRes, userRes] = await Promise.all([
        fetch(`${backendUrl}/api/especialidades`, { headers }),
        fetch(`${backendUrl}/api/resumos`, { headers }),
        fetch(`${backendUrl}/api/auth/me`, { headers }) // NEW: fetch user data
      ]);

      if (espRes.ok) {
        const espData = await espRes.json();
        setEspecialidades(espData);
      }

      if (resumosRes.ok) {
        const resumosData = await resumosRes.json();
        setResumos(resumosData);
      }

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPapel(userData.papel); // NEW: set user role
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      let url = `${backendUrl}/api/resumos?`;
      if (busca) {
        url += `busca=${encodeURIComponent(busca)}&`;
      }
      if (especialidadeFiltro) {
        url += `especialidadeId=${encodeURIComponent(especialidadeFiltro)}`;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setResumos(data);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
    }
  };

  const handleEspecialidadeClick = async (especialidadeFixa) => {
    // Find matching real especialidade from database using dbMatch
    const especialidadeReal = especialidades.find(esp => 
      especialidadeFixa.dbMatch && esp.nome.toLowerCase().includes(especialidadeFixa.dbMatch.toLowerCase())
    );

    if (especialidadeReal) {
      setEspecialidadeModal({ ...especialidadeFixa, realId: especialidadeReal.id });
      await fetchResumosEspecialidade(especialidadeReal.id);
      setEspecialidadeModalOpen(true);
    } else {
      toast.error(`Especialidade ${especialidadeFixa.nome} não encontrada no sistema`);
    }
  };

  const fetchResumosEspecialidade = async (especialidadeId, busca = '') => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      let url = `${backendUrl}/api/resumos?especialidadeId=${especialidadeId}`;
      if (busca) {
        url += `&busca=${encodeURIComponent(busca)}`;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setResumosModal(data);
      }
    } catch (error) {
      console.error('Erro ao buscar resumos:', error);
    }
  };

  const handleBuscaModalChange = (valor) => {
    setBuscaModal(valor);
    if (especialidadeModal) {
      fetchResumosEspecialidade(especialidadeModal.realId, valor);
    }
  };

  const handleFilterChipClick = (especialidadeFixa) => {
    const especialidadeReal = especialidades.find(esp => 
      especialidadeFixa.dbMatch && esp.nome.toLowerCase().includes(especialidadeFixa.dbMatch.toLowerCase())
    );

    if (especialidadeReal) {
      if (especialidadeFiltro === especialidadeReal.id) {
        setEspecialidadeFiltro(null);
      } else {
        setEspecialidadeFiltro(especialidadeReal.id);
      }
    }
  };

  const handlePdfClick = (resumoId) => {
    navigate(`/usuario/resumos/${resumoId}`);
  };

  const getEspecialidadeNome = (especialidadeId) => {
    const esp = especialidades.find(e => e.id === especialidadeId);
    return esp ? esp.nome : 'N/A';
  };

  if (loading) {
    return (
      <div className="resumos-page">
        <div className="resumos-container">
          <p className="text-white text-xl">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="resumos-page">
      {/* Header */}
      <div className="resumos-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/usuario')}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <h1 className="resumos-title">Biblioteca de Resumos</h1>
        <p className="resumos-subtitle">
          Encontre rapidamente os temas por especialidade ou palavra-chave
        </p>
      </div>

      <div className="resumos-container">
        {/* Search Bar */}
        <div className="search-section">
          <div className="search-bar-container">
            <Search className="search-icon" />
            <Input
              placeholder="Busque por tema, palavra-chave ou assunto..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="search-input"
              style={{
                color: '#FFFFFF',
                caretColor: '#FFFFFF'
              }}
            />
          </div>
        </div>

        {/* Specialty Cards Grid */}
        <div className="section-header">
          <h2 className="section-title">Especialidades</h2>
          <p className="section-description">Selecione uma especialidade para filtrar os resumos</p>
        </div>

        <div className="specialty-cards-grid">
          {especialidadesFixas.map((especialidade) => (
            <Card
              key={especialidade.id}
              className="specialty-card-resumo"
              onClick={() => handleEspecialidadeClick(especialidade)}
              style={{
                backgroundImage: `url(${especialidade.imagem})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              <div className="specialty-card-overlay">
                <h3 className="specialty-card-name">{especialidade.nome}</h3>
              </div>
            </Card>
          ))}
        </div>

        {/* Resumos List */}
        <div className="section-header">
          <h2 className="section-title">
            {especialidadeFiltro || busca ? 'Resultados da Pesquisa' : 'Todos os Resumos'}
          </h2>
          <p className="section-description">
            {resumos.length} {resumos.length === 1 ? 'resumo encontrado' : 'resumos encontrados'}
          </p>
        </div>

        {resumos.length === 0 ? (
          <Card className="empty-state">
            <CardContent className="empty-content">
              <FileText className="empty-icon" />
              <p className="empty-text">Nenhum resumo encontrado</p>
              <p className="empty-subtext">Tente ajustar os filtros ou buscar por outros termos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="resumos-list">
            {resumos.map((resumo) => (
              <Card key={resumo.id} className="resumo-card">
                <CardContent className="resumo-content">
                  <div className="resumo-header">
                    <div>
                      <h3 className="resumo-title">{resumo.titulo}</h3>
                      <span className="resumo-especialidade">
                        {getEspecialidadeNome(resumo.especialidadeId)}
                      </span>
                    </div>
                    <Button
                      onClick={() => handlePdfClick(resumo.id)}
                      className="pdf-button"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Ver PDF
                    </Button>
                  </div>
                  <p className="resumo-description">{resumo.descricaoCurta}</p>
                  {resumo.palavrasChave && resumo.palavrasChave.length > 0 && (
                    <div className="keywords-container">
                      {resumo.palavrasChave.map((palavra, idx) => (
                        <span key={idx} className="keyword-tag">
                          {palavra}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Especialidade Modal */}
      <Dialog open={especialidadeModalOpen} onOpenChange={setEspecialidadeModalOpen}>
        <DialogContent className="especialidade-modal">
          <DialogHeader>
            <DialogTitle className="modal-title-esp">
              {especialidadeModal?.nome}
            </DialogTitle>
          </DialogHeader>
          
          <div className="modal-search-container">
            <Search className="modal-search-icon" />
            <Input
              placeholder="Buscar nesta especialidade..."
              value={buscaModal}
              onChange={(e) => handleBuscaModalChange(e.target.value)}
              className="modal-search-input"
            />
          </div>

          <div className="modal-resumos-list">
            {resumosModal.length === 0 ? (
              <div className="modal-empty">
                <FileText className="modal-empty-icon" />
                <p>Nenhum resumo encontrado</p>
              </div>
            ) : (
              resumosModal.map((resumo) => (
                <div key={resumo.id} className="modal-resumo-item">
                  <div className="modal-resumo-content">
                    <h4 className="modal-resumo-title">{resumo.titulo}</h4>
                    <p className="modal-resumo-desc">{resumo.descricaoCurta}</p>
                  </div>
                  <Button
                    onClick={() => {
                      setEspecialidadeModalOpen(false);
                      navigate(`/usuario/resumos/${resumo.id}`);
                    }}
                    className="modal-resumo-button"
                  >
                    Ver resumo
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Modal */}
      <Dialog open={pdfModalOpen} onOpenChange={setPdfModalOpen}>
        <DialogContent className="pdf-modal">
          <DialogHeader>
            <DialogTitle className="modal-title">Visualizar PDF</DialogTitle>
          </DialogHeader>
          <div className="pdf-container">
            <p className="pdf-info">PDF será aberto em uma nova aba</p>
            <Button
              onClick={() => window.open(selectedPdf, '_blank')}
              className="open-pdf-button"
            >
              <Download className="mr-2 h-4 w-4" />
              Abrir PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsuarioResumosNovo;
